import React, { useState } from 'react';
import { supabase, signInWithGoogle, signInWithFacebook } from '../services/supabaseClient';
import { Button } from '../components/ui/Button';

interface AuthProps {
  onLoginSuccess: () => void;
}

export const Auth: React.FC<AuthProps> = ({ onLoginSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);

  // Default admin credentials pre-filled for convenience
  const [email, setEmail] = useState('josferestudio@gmail.com');
  const [password, setPassword] = useState('111111');

  // Campos específicos para registro
  const [fullName, setFullName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'error' | 'success', text: string } | null>(null);

  const handleEmailAuth = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      if (isForgotPassword) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/#reset-password`,
        });
        if (error) throw error;
        setMessage({ type: 'success', text: 'Te hemos enviado un enlace para recuperar tu contraseña.' });
      } else if (isLogin) {
        // Lógica de Inicio de Sesión
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        onLoginSuccess();
      } else {
        // Lógica de Registro con Metadatos (Nombre y WhatsApp)
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName, // Se guardará en raw_user_meta_data y el trigger lo moverá a 'profiles'
              whatsapp: whatsapp || null,
            },
          },
        });
        if (error) throw error;
        setMessage({ type: 'success', text: '¡Registro exitoso! Revisa tu email para confirmar tu cuenta (si es requerido).' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Ocurrió un error durante el proceso.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 bg-mabelita-light flex items-center justify-center">
      <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-gray-100 animate-in fade-in zoom-in duration-300">
        <div className="text-center mb-8">
          <h1 className="font-serif font-bold text-3xl text-mabelita-dark mb-2">
            {isForgotPassword ? 'Recuperar Contraseña' : (isLogin ? 'Bienvenido de nuevo' : 'Únete a la comunidad')}
          </h1>
          <p className="text-gray-500">
            {isForgotPassword ? 'Ingresa tu email para recibir un enlace seguro' : (isLogin ? 'Ingresa a tu cuenta para continuar' : 'Regístrate para comprar y ver contenido exclusivo')}
          </p>
        </div>

        {/* Botones de Social Login (Ocultos en Forgot Password) */}
        {!isForgotPassword && (
          <>
            <div className="space-y-3 mb-8">
              <button
                type="button"
                onClick={signInWithGoogle}
                className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 text-gray-700 py-2.5 rounded-lg hover:bg-gray-50 transition-colors font-medium shadow-sm"
              >
                <img src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-5 h-5" alt="Google" />
                Continuar con Google
              </button>
              <button
                type="button"
                onClick={signInWithFacebook}
                className="w-full flex items-center justify-center gap-3 bg-[#1877F2] text-white py-2.5 rounded-lg hover:bg-[#1864D9] transition-colors font-medium shadow-sm"
              >
                <img src="https://www.svgrepo.com/show/475647/facebook-color.svg" className="w-5 h-5 bg-white rounded-full" alt="Facebook" />
                Continuar con Facebook
              </button>
            </div>

            <div className="relative mb-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">O usa tu email</span>
              </div>
            </div>
          </>
        )}

        {/* Formulario Manual */}
        <form onSubmit={handleEmailAuth} className="space-y-4">
          {message && (
            <div className={`p-3 rounded-lg text-sm text-center font-medium ${message.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
              {message.text}
            </div>
          )}

          {/* Campos exclusivos de Registro */}
          {!isLogin && !isForgotPassword && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Completo <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-mabelita-primary focus:border-transparent outline-none transition-all"
                  placeholder="Ej. María Pérez"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp <span className="text-gray-400 font-normal text-xs">(Opcional)</span></label>
                <input
                  type="tel"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-mabelita-primary focus:border-transparent outline-none transition-all"
                  placeholder="+34 600 000 000"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-mabelita-primary focus:border-transparent outline-none transition-all"
              placeholder="tu@email.com"
            />
          </div>

          {!isForgotPassword && (
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-700">Contraseña <span className="text-red-500">*</span></label>
                {isLogin && (
                  <button type="button" onClick={() => { setIsForgotPassword(true); setMessage(null); }} className="text-xs text-mabelita-primary hover:underline">
                    ¿Olvidaste tu contraseña?
                  </button>
                )}
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-mabelita-primary focus:border-transparent outline-none transition-all"
                placeholder="••••••••"
              />
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full mt-6 shadow-md hover:shadow-lg"
          >
            {loading ? 'Procesando...' : (isForgotPassword ? 'Enviar Enlace' : (isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'))}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm">
          {isForgotPassword ? (
            <button
              onClick={() => { setIsForgotPassword(false); setIsLogin(true); setMessage(null); }}
              className="font-semibold text-mabelita-primary hover:text-mabelita-secondary transition-colors"
            >
              Volver a Iniciar Sesión
            </button>
          ) : (
            <>
              <span className="text-gray-500">
                {isLogin ? '¿No tienes cuenta?' : '¿Ya tienes cuenta?'}
              </span>
              <button
                onClick={() => { setIsLogin(!isLogin); setMessage(null); }}
                className="ml-2 font-semibold text-mabelita-primary hover:text-mabelita-secondary transition-colors"
              >
                {isLogin ? 'Regístrate aquí' : 'Inicia sesión aquí'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
